import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getOrders,
  cancelOrder,
  rejectOrder,
  getOrderById,
  failOrderDelivery,
  delayOrder,
  requestDriverChange,
} from "services/order";
import PaginatedTable from "Components/shared/PaginatedTable";
import { orderStatus } from "enums/shared";
import { useCallback, useMemo, useState, useEffect } from "react";
import OrderSearch from "./orderSearch";
import formatToUTC from "utils/formatToUTC";
import CustomSnackbar from "Components/shared/CustomSnackbar";
import { useTranslation } from "react-i18next";
import formatDateForInput from "utils/formatDateForInput";
import { debounce } from "utils/debounce";
import { orderActionsMap } from "config/orderActions";
import { fallbackValue } from "constants/fallbackConstant";
import {
  ordersFiltersAtom,
  ordersPageAtom,
  ordersRowsPerPageAtom,
} from "./atoms/ordersFiltersAtom";
import { useAtom } from "jotai";
import RejectOrderModal from "./RejectOrderModal";
import DeliveryFailureModal from "./DeliveryFailureModal";
import DelayOrderModal from "./DelayOrderModal";
import DriverChangeRequestModal from "./DriverChangeRequestModal";

/**
 * Renders the OrderTable component, displaying a paginated table of orders
 * with filters, actions, and a search bar.
 *
 */
const OrderTable = ({ columns = [], senderId = null, hideSearch = false }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useAtom(ordersPageAtom);
  const [rowsPerPage, setRowsPerPage] = useAtom(ordersRowsPerPageAtom);
  const { t, i18n } = useTranslation();

  const [modalState, setModalState] = useState({
    open: false,
    type: null,
    order: null,
  });

  const location = useLocation();
  const queryState = location.state || {}; //  access status, the status is not a part of the url but it hold it

  const initialStatus = (() => {
    const s = queryState.status;

    if (Array.isArray(s)) {
      return s.every((v) => typeof v === "number") ? s : [Number(s[0])];
    }

    const numericMatch = Object.entries(orderStatus).find(
      ([, value]) => value.value === s
    );

    if (!isNaN(s)) return [Number(s)];
    if (numericMatch) return [Number(numericMatch[0])];

    return null;
  })();

  useEffect(() => {
    if (initialStatus) {
      setFilters((prev) => ({
        ...prev,
        status: initialStatus,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [filters, setFilters] = useAtom(ordersFiltersAtom);

  const isRTL = i18n.language === "ar";

  columns = [
    { id: "orderNumber", label: t("orderNumber"), sortable: true },
    { id: "senderName", label: t("senderName"), sortable: true }, // this column will not be display in SenderOrdersTab
    { id: "receiverName", label: t("reciverName"), sortable: true },
    { id: "driverName", label: t("driverName"), sortable: true },
    { id: "pickUpLocation", label: t("pickUpLocation"), sortable: true },
    { id: "pickupDateTime", label: t("pickupDateTime"), sortable: true },
    { id: "dropOffLocation", label: t("dropOffLocation"), sortable: true },
    { id: "dropOffDateTime", label: t("dropOffDateTime"), sortable: true },
    { id: "fanoosFees", label: t("fanoosFees"), sortable: true },
    { id: "driverFees", label: t("driverFees"), sortable: true },
    { id: "orderFees", label: t("orderAmount"), sortable: true },
    { id: "deliveryFees", label: t("deliveryFees"), sortable: true },
    { id: "creationDate", label: t("creationDate"), sortable: true },
    { id: "status", label: t("status"), sortable: true, name: "orderStatus" },
    { id: "action", label: t("action") },
  ];
  const [snackbarDetails, setSnackbarDetails] = useState({
    message: "",
    severity: "success",
  });

  /**
   * Styles for custom columns based on the current language direction.
   * @type {Object}
   */
  const customColumnStyles = {
    action: {
      textAlign: isRTL ? "left" : "right",
    },
  };

  const fetchOrders = async () => {
    const {
      status,
      fromDate,
      toDate,
      pickupDate,
      dropoffDate,
      creationDate,
      senderName,
      orderNumber,
      receiverName,
      driverName,
      pickUpLocation,
      dropOffLocation,
      sortDescending,
      orderType,
    } = filters;

    const validFrom = fromDate ? formatToUTC(fromDate) : null;
    const validTo = toDate ? formatToUTC(toDate) : null;
    const validPickupDate = pickupDate ? formatToUTC(pickupDate) : null;
    const validDropoffDate = dropoffDate ? formatToUTC(dropoffDate) : null;
    const validCreationDate = creationDate ? formatToUTC(creationDate) : null;

    return getOrders(
      rowsPerPage,
      page + 1,
      status ?? [],
      senderName,
      orderNumber,
      receiverName,
      driverName,
      pickUpLocation,
      dropOffLocation,
      sortDescending,
      validFrom,
      validTo,
      validPickupDate,
      validDropoffDate,
      validCreationDate,
      i18n.language,
      senderId,
      orderType
    );
  };
  const [lastUpdated, setLastUpdated] = useState(Date.now());
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      "orders",
      { page: page + 1, pageSize: rowsPerPage, ...filters, senderId },
    ],
    queryFn: fetchOrders,
    onSuccess: () => {
      setLastUpdated(Date.now());
    },
    select: (data) => ({
      rows: data.page.map((order) => ({
        ...order,
        creationDate: order.creationDate
          ? formatDateForInput(order.creationDate)
          : fallbackValue,
        fanoosFees:
          order.fanoosFees ??
          order.FanoosFees ??
          order.fanoosFee ??
          order.FanoosFee ??
          fallbackValue,
        driverFees:
          order.driverFees ??
          order.DriverFees ??
          order.driverFee ??
          order.DriverFee ??
          fallbackValue,
        orderFees:
          order.orderFees ??
          order.OrderFees ??
          order.orderFee ??
          order.OrderFee ??
          fallbackValue,
        deliveryFees:
          order.deliveryFees ??
          order.DeliveryFees ??
          order.deliveryFee ??
          order.DeliveryFee ??
          fallbackValue,
        status: t(orderStatus[order.status]?.value || "Unknown"),
        driverName: order.driverName || t("-"),
        //showing the time with the date
        pickupDateTime: order.preferredPickUpDate
          ? formatDateForInput(order.preferredPickUpDate)
          : fallbackValue,
        dropOffDateTime: order.preferredDeliveryDate
          ? formatDateForInput(order.preferredDeliveryDate)
          : fallbackValue,
        pickUpLocation: order.pickUpLocation
          ? ` ${order.pickUpLocation.area || ""}`
          : "-",
        dropOffLocation: order.dropOffLocation
          ? ` ${order.dropOffLocation.area || ""}`
          : "-",
      })),
      totalRecords: data.totalRecords,
    }),
    keepPreviousData: true,
  });

  /**
   * Handle the cancelation order and shows success/error notifications.
   */
  const cancelOrderMutation = useMutation({
    mutationFn: cancelOrder,
    onSuccess: () => {
      setSnackbarDetails({
        message: "Order canceled successfully!",
        severity: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: () => {
      setSnackbarDetails({
        message: "Failed to cancel order.",
        severity: "error",
      });
    },
  });

  const handleSnackbarClose = () => {
    setSnackbarDetails({ message: "", severity: "success" });
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  /**
   * Updates the number of rows displayed per page.
   * @param {Object} event - The triggering event.
   */
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const debouncedUpdateFilters = useMemo(
    () =>
      debounce((newFilters) => {
        setFilters((prev) => ({ ...prev, ...newFilters }));
        setPage(0);
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const setDebouncedFilters = useCallback(
    (newFilters) => {
      debouncedUpdateFilters(newFilters);
    },
    [debouncedUpdateFilters]
  );

  const rejectOrderMutation = useMutation({
    mutationFn: ({ orderId, driverId, reason }) =>
      rejectOrder(orderId, driverId, reason),
    onSuccess: () => {
      setSnackbarDetails({
        message: t("orderRejectedSuccessfully"),
        severity: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      closeModal();
    },
    onError: (error) => {
      setSnackbarDetails({
        message: error.message || t("failedToRejectOrder"),
        severity: "error",
      });
    },
  });

  const deliveryFailureMutation = useMutation({
    mutationFn: ({ orderId, driverId, reason }) =>
      failOrderDelivery(orderId, driverId, reason),
    onSuccess: () => {
      setSnackbarDetails({
        message: t("deliveryFailureSuccess"),
        severity: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      closeModal();
    },
    onError: (error) => {
      setSnackbarDetails({
        message: error.message || t("deliveryFailureError"),
        severity: "error",
      });
    },
  });

  const delayOrderMutation = useMutation({
    mutationFn: ({ orderId, driverId, payload }) =>
      delayOrder(orderId, driverId, payload),
    onSuccess: () => {
      setSnackbarDetails({
        message: t("orderDelayedSuccessfully"),
        severity: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      closeModal();
    },
    onError: (error) => {
      setSnackbarDetails({
        message: error.message || t("failedToDelayOrder"),
        severity: "error",
      });
    },
  });

  const driverChangeRequestMutation = useMutation({
    mutationFn: requestDriverChange,
    onSuccess: () => {
      setSnackbarDetails({
        message: t("driverChangeRequestSuccess"),
        severity: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      closeModal();
    },
    onError: (error) => {
      setSnackbarDetails({
        message: t("driverChangeRequestError"),
        severity: "error",
      });
    },
  });

  const openModal = async (type, order) => {
    try {
      const fullOrderDetails = await getOrderById(order.id);
      setModalState({ open: true, type, order: fullOrderDetails });
    } catch (error) {
      console.error("Failed to fetch order details:", error);
      setSnackbarDetails({
        message: t("failedToFetchOrderDetails"),
        severity: "error",
      });
    }
  };
  const closeModal = () => {
    setModalState({ open: false, type: null, order: null });
  };

  const handleRejectSubmit = (reason) => {
    if (modalState.order) {
      const { id: orderId, driverId } = modalState.order;

      if (!driverId) {
        setSnackbarDetails({
          message: t("noDriverAssignedToOrder"),
          severity: "error",
        });
        return;
      }

      rejectOrderMutation.mutate({ orderId, driverId, reason });
    }
  };

  const handleDeliveryFailureSubmit = async (reason) => {
    if (modalState.order) {
      const { id: orderId, driverId } = modalState.order;

      if (!driverId) {
        setSnackbarDetails({
          message: t("noDriverAssignedToOrder"),
          severity: "error",
        });
        return;
      }
      deliveryFailureMutation.mutate({ orderId, driverId, reason });
    }
  };

  const handleDelaySubmit = async (payload) => {
    if (modalState.order) {
      try {
        const orderDetails = await getOrderById(modalState.order.id);
        const { id: orderId, driverId } = orderDetails;

        if (!driverId) {
          setSnackbarDetails({
            message: t("noDriverAssignedToOrder"),
            severity: "error",
          });
          return;
        }

        delayOrderMutation.mutate({ orderId, driverId, payload });
      } catch (error) {
        setSnackbarDetails({
          message: t("failedToFetchOrderDetails"),
          severity: "error",
        });
      }
    }
  };

  const handleDriverChangeSubmit = async (payload) => {
    if (modalState.order) {
      driverChangeRequestMutation.mutate({
        orderId: modalState.order.id,
        ...payload,
      });
    }
  };

  const actions = (row) => {
    if (!row) return [];

    // Default actions (e.g., View, Cancel)
    const defaultActions = [
      {
        label: t("view"),
        onClick: () => navigate(`/orders/${row.id}`),
      },
    ];

    if (
      row.status !== "Cancelled" &&
      row.status !== "Delivered" &&
      row.status !== "Failed"
    ) {
      defaultActions.push({
        label: t("cancel"),
        onClick: () => cancelOrderMutation.mutate(row.id),
      });
    }

    const statusActions = orderActionsMap(t, openModal)[row.status] || [];

    return [
      ...defaultActions,
      ...statusActions.map((action) => ({
        label: action.label,
        onClick: () => action.onClick(row, setSnackbarDetails, queryClient),
      })),
    ];
  };

  const handleRowClick = (row) => {
    navigate(`/orders/${row.id}`);
  };

  const statusOptions = [
    { value: null, label: t("All") },
    ...Object.entries(orderStatus).map(([key, value]) => ({
      value: Number(key),
      label: t(value.value),
    })),
  ];

  const applyFiltersImmediately = (newFilters) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
  };

  const applyFiltersWithDebounce = (newFilters) => {
    const updatedFilters = { ...filters, ...newFilters };
    setDebouncedFilters(updatedFilters); // Only debounced
  };
  const handleStatusChange = (value) => {
    const normalized = Array.isArray(value) ? value : [value];

    const cleaned =
      normalized.includes(null) || normalized.includes(0) ? null : normalized;

    applyFiltersImmediately({ status: cleaned });
  };

  // Update the URL query parameter when the page state changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", page);
    setSearchParams(newSearchParams, { replace: true });
  }, [page, searchParams, setSearchParams]);

  const handleSortChange = (column, isDescending) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      sortDescending: isDescending,
    }));
  };

  const handleSearch = (value, columnId) => {
    if (
      [
        "orderNumber",
        "senderName",
        "receiverName",
        "driverName",
        "pickUpLocation",
        "dropOffLocation",
      ].includes(columnId)
    ) {
      applyFiltersWithDebounce({ [columnId]: value });
    }
  };

  const handleDateChange = (key, date) => {
    const updatedFilters = { ...filters, [key]: date };
    setFilters(updatedFilters);
  };

  if (isError) {
    return <div>Error loading orders. Please try again later.</div>;
  }
  return (
    <>
      {!hideSearch && (
        <OrderSearch
          fetchDataHandler={setDebouncedFilters}
          initialFilters={{ status: initialStatus }}
          lastUpdated={lastUpdated}
        />
      )}{" "}
      <PaginatedTable
        columns={columns}
        rows={data?.rows || []}
        isLoading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        customColumnStyles={customColumnStyles}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        onRowClick={handleRowClick}
        actions={actions}
        totalRecords={data?.totalRecords || 0}
        filters={filters}
        ordrStatusOptions={statusOptions}
        handleStatusChange={handleStatusChange}
        onSortChange={handleSortChange}
        onSearch={handleSearch}
        handleDateChange={handleDateChange}
      />
      <CustomSnackbar
        message={snackbarDetails.message}
        severity={snackbarDetails.severity}
        onClose={handleSnackbarClose}
      />
      {modalState.type === "reject" && (
        <RejectOrderModal
          open={modalState.open}
          onClose={closeModal}
          onSubmit={handleRejectSubmit}
          t={t}
          isRTL={isRTL}
          modificationDate={modalState.order?.modificationDate}
        />
      )}
      {modalState.type === "deliveryFailure" && (
        <DeliveryFailureModal
          open={modalState.open}
          onClose={closeModal}
          onSubmit={handleDeliveryFailureSubmit}
          t={t}
          isRTL={isRTL}
          modificationDate={modalState.order?.modificationDate}
        />
      )}
      {modalState.type === "delay" && (
        <DelayOrderModal
          open={modalState.open}
          onClose={closeModal}
          onSubmit={handleDelaySubmit}
          t={t}
          isRTL={isRTL}
        />
      )}
      {modalState.type === "driverChangeRequest" && (
        <DriverChangeRequestModal
          open={modalState.open}
          onClose={closeModal}
          onSubmit={handleDriverChangeSubmit}
          t={t}
          isRTL={isRTL}
        />
      )}
    </>
  );
};

export default OrderTable;
